// One poll per endpoint for the whole app, however many components show it (the bell, the pop-ups, both side
// menus...). It runs only while something is subscribed and the tab is visible; coming back to the tab refreshes at
// once if the data is older than a few seconds. Cuts the background /api/notifications, /api/me/mentions and
// /api/trade-plans/reconcile traffic that kept running in hidden tabs.
import { useEffect, useState } from 'react';

const FRESH_ON_RETURN_MS = 5000;

/** A poller for `load()` (a function returning a promise of the data) every `everyMs` while visible. */
export function createPoller(load, everyMs) {
  const subs = new Set();
  let data;
  let at = 0;
  let timer = null;
  let inflight = null;

  const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

  const refresh = () => {
    if (inflight) return inflight;
    inflight = Promise.resolve()
      .then(load)
      .then((d) => {
        if (d !== undefined) {
          data = d;
          at = Date.now();
          subs.forEach((cb) => cb(d));
        }
      })
      .catch(() => {})
      .finally(() => { inflight = null; });
    return inflight;
  };

  const tick = () => { if (visible()) refresh(); };
  const onReturn = () => { if (visible() && Date.now() - at > FRESH_ON_RETURN_MS) refresh(); };

  const start = () => {
    timer = setInterval(tick, everyMs);
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
  };
  const stop = () => {
    clearInterval(timer);
    timer = null;
    document.removeEventListener('visibilitychange', onReturn);
    window.removeEventListener('focus', onReturn);
  };

  return {
    subscribe(cb) {
      subs.add(cb);
      if (subs.size === 1) start();
      if (data !== undefined) cb(data);
      if (Date.now() - at > FRESH_ON_RETURN_MS) refresh();
      return () => {
        subs.delete(cb);
        if (!subs.size) stop();
      };
    },
    refresh,
    get data() { return data; },
  };
}

/** The poller's latest data in a component (undefined until the first load). */
export function usePoller(poller) {
  const [data, setData] = useState(poller.data);
  useEffect(() => poller.subscribe(setData), [poller]);
  return data;
}
